import './decomp.component.scss';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { ButtonGroup } from 'react-bootstrap';
import { ButtonDefault } from '../../components/buttons/button-default.component';
import { AppErrorBoundaryComponent } from '../../components/error-boundary/app-error-boundary.component';
import { FilterButtonDropdownAdvComponent } from '../../components/filters/components/filter-button-dropdown/filter-button-dropdown-adv.component';
import { FilterPaneHeaderComponent } from '../../components/filters/components/filter-pane-header/filter-pane-header.component';
import { FilterPaneTopComponent } from '../../components/filters/components/filter-pane-header/filter-pane-top.component';
import {
  hasFilterSelectedValue,
  useAppSingleFilterSelectedLabel,
  useConcatenateFilterSelection,
  useFilterSelectionMemo,
  useFindFilterSelection,
} from '../../components/filters/hooks/common.hook';
import { FILTER_NAMES } from '../../components/filters/hooks/filter-names.constants';
import { useFilterVariableGroupSelection } from '../../components/filters/hooks/report-filter-selection.hook';
import {
  useFilterCoefficientSelection,
  useFilterDataZoomSelection,
  useFilterVariableCompBrandSelection,
} from '../../components/filters/hooks/use-filter-drillthrough-selection.hook';
import { useFilterContext } from '../../components/filters/provider/filter.provider';
import { useZoomDate } from '../../components/reports/common/hooks/use-zoom-date-filter.hook';
import { useZoomResetOnFilterSelection } from '../../components/reports/common/hooks/use-zoom.hook';
import {
  ExportButtonComponent,
  ExportButtonDownloader,
} from '../../components/reports/components/export-button/export-button.component';
import { ShareButtonComponent } from '../../components/reports/components/share-button/share-button.component';
import { REPORT_NAMES } from '../../components/reports/constants/report-names.constants';
import { ReportsComponentProps } from '../../components/reports/interfaces/reports-component-props';

import { LastRunModelComponent } from '../../components/reports/last-run-model/last-run-model-component';
import { useDataProvider } from '../../hooks/data-provider/data-provider.hook';
import { useAppFindFilter } from '../../hooks/filters/use-app-filters.hook';
import { useLastAvailableDate } from '../../hooks/last-available-date/use-last-available-date.hook';
import { DEFAULT_DATE_FORMAT } from '../../utils/formatting/date-formatter.helper';
import { BodyContentComponent } from '../layout/components/body-content/body-content.component';
import { BodySectionComponent } from '../layout/components/body-section/body-section.component';
import { BodyTopBarComponent } from '../layout/components/body-top-bar/body-top-bar.component';
import { CoefficientV2Component } from './components/coefficient/coefficient-v2.component';
import DecompModelChartComponent from './components/decomp-model-chart/decomp-model-chart-component';
import { DecompModelTableComponent } from './components/decomp-model-table/decomp-model-table.component';
import { DecompModelWaterfallComponent } from './components/decomp-model-waterfall/charts/decomp-model-waterfall.component';

export const DecompComponent = ({
  filterSelection,
  enabled,
}: ReportsComponentProps) => {
  const [PDFDownloader, setPDFDownloader] = useState<
    ExportButtonDownloader | undefined
  >(undefined);

  const filterSelectionCards = useConcatenateFilterSelection(
    useFilterVariableGroupSelection(),
    filterSelection,
  );

  const filterSelectionLineChart = useConcatenateFilterSelection(
    useFilterVariableGroupSelection(),
    useFilterVariableCompBrandSelection(),
    filterSelection,
  );
  const filterSelectionTable = useConcatenateFilterSelection(
    useFilterCoefficientSelection(),
    filterSelection,
  );
  const filterSelectionWaterfall = useConcatenateFilterSelection(
    useFilterDataZoomSelection(),
    filterSelection,
  );

  const filterSelectionExportButton = useConcatenateFilterSelection(
    useFilterDataZoomSelection(),
    useFilterCoefficientSelection(),
    filterSelectionLineChart,
  );

  const [showTable, setShowTable] = useState(false);
  const [showWfTable, setShowWfTable] = useState(false);
  const reportTitle = useAppSingleFilterSelectedLabel(FILTER_NAMES.MARKET);

  const filtersData = useFilterContext();
  const coeffSelectorFilter = useAppFindFilter(
    filtersData,
    FILTER_NAMES.COEFFICIENTSELECTOR,
  );
  const {
    dateZoomValue,
    resetZoomFunction,
    setResetZoomCallback,
    setZoom,
    zoom,
  } = useZoomDate();
  const dateZoomLabel = useMemo(() => {
    const selVal = hasFilterSelectedValue(dateZoomValue);
    if (selVal) {
      const startDate = moment(selVal.values[0].value).format(
        DEFAULT_DATE_FORMAT,
      );
      const endDate = moment(selVal.values[1].value).format(
        DEFAULT_DATE_FORMAT,
      );
      if (startDate !== endDate) {
        return ` | ${startDate} to ${endDate}`;
      } else {
        return ` | ${endDate}`;
      }
    }
    return '';
  }, [dateZoomValue]);
  // Reset zoom when filter selection changes.
  useZoomResetOnFilterSelection(filterSelection, resetZoomFunction);

  const { data: coefficientHelpData } = useDataProvider<Record<string, any>>(
    REPORT_NAMES.COEFFICIENT_HELP,
    filterSelectionCards,
    [],
    enabled,
    false,
  );

  // Only Market filter is required to know last run date.
  const filterSelectionForLastRun = useFilterSelectionMemo(
    ...useFindFilterSelection(
      filterSelection,
      (i) => i.id === FILTER_NAMES.MARKET,
    ),
  );
  const lastAvailableDate = useLastAvailableDate(filterSelectionForLastRun);

  return (
    <>
      <BodyTopBarComponent>
        <div className="d-flex top-filter-container dateadjustments  m-d-block">
          <AppErrorBoundaryComponent>
            <FilterPaneTopComponent
              filterSelection={filterSelectionForLastRun}
              lastAvailableDate={lastAvailableDate}
            />
          </AppErrorBoundaryComponent>
        </div>
        <div className="top-filter-container ms-auto d-flex">
          <div className="ps-3">
            <div className="filter-pane-header-container">
              <FilterPaneHeaderComponent
                filterSelection={filterSelectionCards}
                filterIds={[
                  FILTER_NAMES.MEASUREHIER,
                  FILTER_NAMES.DATEHIER,
                  FILTER_NAMES.VARIABLEGROUP,
                ]}
              />
            </div>
          </div>
          <div className="ps-3">
            <ShareButtonComponent filterSelection={filterSelection} />
          </div>

          <div className="ps-3 d-flex align-items-center">
            <ExportButtonComponent
              PDFDownloader={PDFDownloader}
              reportName={`${REPORT_NAMES.COEFFICIENT_V1},${REPORT_NAMES.DECOMP_MODEL_CHART},${REPORT_NAMES.DECOMP_MODEL},${REPORT_NAMES.DECOMP_MODEL_WATERFALL_CHART}`}
              filterSelection={filterSelectionExportButton}
              filename="IRIS-Decomp-Model"
            />
          </div>
        </div>
      </BodyTopBarComponent>

      <BodyContentComponent>
        <BodySectionComponent className="pb-0">
          <AppErrorBoundaryComponent>
            <CoefficientV2Component
              title="Selected Total:"
              className="coefficient-report"
              reportName={REPORT_NAMES.COEFFICIENT_V2}
              enabled={enabled}
              filterSelection={filterSelectionCards}
              coefficientHelpData={coefficientHelpData}
            >
              {coeffSelectorFilter && (
                <div className="filter-pane-header-item">
                  <FilterButtonDropdownAdvComponent
                    variant="stand-alone"
                    filter={coeffSelectorFilter}
                    filterSelection={filterSelectionCards}
                    disabled={!enabled}
                  />
                </div>
              )}
            </CoefficientV2Component>
          </AppErrorBoundaryComponent>
        </BodySectionComponent>
        <BodySectionComponent>
          <div className="container-fluid decomp-model-wrapper report-container">
            <div className="row">
              <div className="col-12">
                <div className="d-flex report-title-container">
                  <div className="d-block report-title">{reportTitle} </div>
                  {zoom && (
                    <div className="d-block ms-auto">
                      <ButtonGroup aria-label="">
                        <ButtonDefault variant="sm" onClick={resetZoomFunction}>
                          Clear Selected Range
                        </ButtonDefault>
                      </ButtonGroup>
                    </div>
                  )}
                  <div className="d-flex ms-auto last-run-container">
                    <AppErrorBoundaryComponent>
                      <LastRunModelComponent
                        reportName="Decomp Model"
                        filterSelection={filterSelectionForLastRun}
                        enabled={enabled}
                      />
                    </AppErrorBoundaryComponent>
                  </div>
                </div>
                <AppErrorBoundaryComponent>
                  <DecompModelChartComponent
                    setZoom={setZoom}
                    setResetZoomCallback={setResetZoomCallback}
                    reportName={REPORT_NAMES.DECOMP_MODEL_CHART}
                    filterSelection={filterSelectionLineChart}
                    enabled={enabled}
                    defaultSortBy={[{ id: 'WEEK', desc: false }]}
                    setPDFDownloader={setPDFDownloader}
                  />
                </AppErrorBoundaryComponent>
              </div>
              <div className="col-12">
                <div className="ps-3 pe-3 pb-3">
                  <ButtonGroup aria-label="">
                    <ButtonDefault
                      variant="sm"
                      id="toggleTableView"
                      value={'tableView'}
                      active={showTable}
                      onClick={() => setShowTable(!showTable)}
                    >
                      View Table
                    </ButtonDefault>
                  </ButtonGroup>
                </div>
              </div>
              {showTable && (
                <div className="col-12">
                  <DecompModelTableComponent
                    enabled={enabled}
                    height={500}
                    filterSelection={filterSelectionTable}
                    reportName={REPORT_NAMES.DECOMP_MODEL}
                    totalReportId={REPORT_NAMES.DECOMP_MODEL_TOTAL}
                    defaultSortBy={[{ id: 'WEEK', desc: false }]}
                    className="decomp-model-table-component report-container"
                  />
                </div>
              )}
            </div>
          </div>
        </BodySectionComponent>
        <BodySectionComponent>
          <div className="container-fluid decomp-model-wrapper report-container">
            <div className="row gy-3">
              <div className="col-12">
                <div className="report-title-container">
                  <div className="report-title">
                    {reportTitle} {dateZoomLabel}
                  </div>
                </div>
                <AppErrorBoundaryComponent>
                  <DecompModelWaterfallComponent
                    filterSelection={filterSelectionWaterfall}
                    reportName={REPORT_NAMES.DECOMP_MODEL_WATERFALL_CHART}
                    defaultSortBy={[
                      { id: 'WATERFALL_ORDER', desc: false },
                      { id: 'WEEK', desc: false },
                      { id: 'DECOMP_IMPACT', desc: false },
                    ]}
                    className="decomp-model-waterfall-chart-component"
                    enabled={enabled}
                  />
                </AppErrorBoundaryComponent>
              </div>
              <div className="col-12">
                <div className="ps-3 pe-3 pb-3">
                  <ButtonGroup aria-label="">
                    <ButtonDefault
                      variant="sm"
                      id="toggleTableView"
                      value={'tableView'}
                      active={showTable}
                      onClick={() => setShowWfTable(!showWfTable)}
                    >
                      View Table
                    </ButtonDefault>
                  </ButtonGroup>
                </div>
              </div>
              {showWfTable && (
                <div className="col-12">
                  <AppErrorBoundaryComponent>
                    <DecompModelTableComponent
                      enabled={enabled}
                      height={400}
                      filterSelection={filterSelectionWaterfall}
                      reportName={REPORT_NAMES.DECOMP_MODEL_WATERFALL}
                      totalReportId={REPORT_NAMES.DECOMP_MODEL_WATERFALL_TOTAL}
                      defaultSortBy={[{ id: 'WATERFALL_ORDER', desc: false }]}
                      className="decomp-model-table-component"
                    />
                  </AppErrorBoundaryComponent>
                </div>
              )}
            </div>
          </div>
        </BodySectionComponent>
      </BodyContentComponent>
    </>
  );
};
export default DecompComponent;
